/**
 * 이메일 미인증 사용자가 콘솔에 들어왔을 때 메인 영역을 덮는 게이트 화면.
 *
 * 백엔드의 EmailVerifiedGuard 가 거의 모든 endpoint 를 403 으로 막기 때문에,
 * 미인증 상태로 콘솔을 들어와봐야 빈 화면 + 에러 토스트 만 잔뜩 나옴.
 * 그럴 바엔 처음부터 명확하게 "메일 인증부터 해주세요" 안내가 깔끔.
 */
import { useState } from "react";
import { Mail, RefreshCw, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  apiClient,
  extractErrorMessage,
  resendVerification,
} from "@/lib/api-client";
import { useAuthStore, type Account } from "@/stores/auth-store";

export function EmailVerificationGate(): JSX.Element {
  const account = useAuthStore((s) => s.account);
  const signout = useAuthStore((s) => s.signout);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const onResend = async (): Promise<void> => {
    setSending(true);
    setError(null);
    try {
      await resendVerification();
      setSent(true);
    } catch (err) {
      setError(extractErrorMessage(err, "메일 발송에 실패했습니다."));
    } finally {
      setSending(false);
    }
  };

  // 사용자가 메일 링크 클릭 후 돌아왔을 때 — /auth/me 다시 불러서 상태 갱신.
  const onRefresh = async (): Promise<void> => {
    setRefreshing(true);
    try {
      const { data } = await apiClient.get<Account>("/auth/me");
      useAuthStore.setState({ account: data });
    } catch {
      // 토큰 만료 등 — 무시. 사용자가 /app 라우팅 가드에서 처리됨.
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div className="flex min-h-[calc(100vh-3.5rem)] items-center justify-center px-4 py-12">
      <div className="w-full max-w-md rounded-lg border border-border bg-card p-8 text-center">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
          <Mail className="h-7 w-7 text-primary" aria-hidden />
        </div>
        <h1 className="mt-5 text-xl font-bold tracking-tight">
          이메일 인증을 마쳐주세요
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          <strong>{account?.email}</strong> 로 보낸 인증 메일의 링크를 누르면
          devit 의 모든 기능을 사용할 수 있어요.
        </p>

        {sent && (
          <p className="mt-5 rounded-md border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-600 dark:text-emerald-400">
            인증 메일을 다시 보냈어요. 받은편지함을 확인해 주세요.
          </p>
        )}
        {error && (
          <p className="mt-5 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {error}
          </p>
        )}

        <div className="mt-6 space-y-2">
          <Button
            type="button"
            className="w-full"
            onClick={onRefresh}
            disabled={refreshing}
          >
            <RefreshCw
              className={refreshing ? "h-4 w-4 animate-spin" : "h-4 w-4"}
              aria-hidden
            />
            인증 완료했어요
          </Button>
          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={onResend}
            disabled={sending || sent}
          >
            <Mail className="h-4 w-4" aria-hidden />
            {sending ? "보내는 중…" : "인증 메일 재발송"}
          </Button>
          <Button
            type="button"
            variant="ghost"
            className="w-full"
            onClick={signout}
          >
            <LogOut className="h-4 w-4" aria-hidden />
            로그아웃
          </Button>
        </div>

        <p className="mt-6 text-xs text-muted-foreground">
          메일이 도착하지 않으면 스팸함을 확인하거나, 잠시 뒤 재발송해 주세요.
        </p>
      </div>
    </div>
  );
}
